import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { adaptCIAMUsers } from "../../utils/adapters";
import {
  API_URL_CIAM_USER_CREATE,
  listCIAMUsersPendingCreation,
  API_URL_USER_LIST,
} from "../../actions";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import {
  DEFAULT_SORTING,
  List,
  PLATFORM_ACCESS_SORT_OPTIONS,
  type RejectModalOptions,
} from "./components/List";
import client from "../../client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { WFPButton } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { CircleCheck, Search, TriangleAlert, UserCheck, X } from "lucide-react";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { SortMenu } from "@/components/SortMenu";

export interface CIAMUser {
  added_by?: string | null;
  country?: string | null;
  date_joined?: string | null;
  email: string;
  first_name: string;
  id: number;
  last_name: string;
  user_profile: string;
  [key: string]: unknown;
}

interface Props {
  isFetching: boolean;
  errorMessage: string;
  records: CIAMUser[];
  listCIAMUsersPendingCreation: () => void;
}

type NotificationType = "success" | "error";

interface notificationStateInterface {
  state: boolean;
  message: ReactNode;
  type: NotificationType;
  detail?: string;
}

interface HttpError {
  message?: string;
  response?: {
    data?: unknown;
    status?: number;
  };
}

interface PendingCreationData {
  users?: CIAMUser[];
}

interface PendingCreationState {
  data?: PendingCreationData | null;
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  listCIAMUsersPendingCreation: PendingCreationState;
}

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

// CIAM failures are surfaced as "... [503]: ..." — pull the status code out of the message.
const extractStatusCode = (message?: string): number | undefined => {
  const match = message?.match(/\[(\d{3})\]/);
  return match ? Number(match[1]) : undefined;
};

const isServerErrorStatus = (status: number | undefined): boolean =>
  status === undefined || status >= 500;

export const PlatformAccess = ({
  errorMessage,
  isFetching,
  listCIAMUsersPendingCreation,
  records,
}: Props) => {
  const { t } = useTranslation();

  const [inProgress, setInProgress] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [rejectModalOptions, setRejectModalOptions] = useState<RejectModalOptions>({
    isOpen: false,
    id: undefined,
  });
  const [notificationState, setNotificationState] = useState<notificationStateInterface>({
    state: false,
    message: "",
    type: "error",
  });

  const closeNotification = () => setNotificationState(current => ({ ...current, state: false }));

  const startNotification = (message: ReactNode, type: NotificationType, detail?: string) => {
    setNotificationState({ state: true, message, type, detail });
    // Success notifications auto-dismiss; errors stay until the user closes them
    // so the (often technical) detail remains readable.
    if (type === "success") {
      return setTimeout(closeNotification, 5000);
    }
  };

  const notifyApproveError = (email: string, isServerError: boolean, detail?: string) => {
    const message: ReactNode = isServerError ? (
      <Trans
        i18nKey="platformAccess.approveServerError"
        values={{ email }}
        components={{ bold: <strong className="font-semibold" /> }}
      />
    ) : (
      t("platformAccess.approveError", { email })
    );
    startNotification(message, "error", detail);
  };

  const notifyRejectError = (email: string, detail?: string) => {
    startNotification(
      <Trans
        i18nKey="platformAccess.rejectServerError"
        values={{ email }}
        components={{ bold: <strong className="font-semibold" /> }}
      />,
      "error",
      detail
    );
  };

  useEffect(() => {
    listCIAMUsersPendingCreation();
  }, [listCIAMUsersPendingCreation]);

  const submitUser = async (user_id: number) => {
    setInProgress(true);
    const user = records.find(user => user.id === user_id) || { email: "User not found" };
    try {
      const response = await client.post(API_URL_CIAM_USER_CREATE, { user_id });
      if (response.data && response.data.ciam_user_created) {
        listCIAMUsersPendingCreation();
        startNotification(t("platformAccess.approveSuccess", { email: user.email }), "success");
      } else {
        // NOTE: this endpoint always responds with HTTP 200, even when CIAM fails.
        // The real CIAM status (e.g. 503) is only available embedded in error_msg
        // (e.g. "... [503]: ..."), so we parse it out rather than reading response.status.
        const detail = response.data.error_msg;
        notifyApproveError(user.email, isServerErrorStatus(extractStatusCode(detail)), detail);
      }
    } catch (e: unknown) {
      const error = e as HttpError;
      // Reached only on a transport-level failure (network error or a non-200 HTTP
      // status), so here the HTTP status from the response object is meaningful.
      const status = error.response?.status;
      const detail =
        error.response && error.response.data && typeof error.response.data === "string"
          ? `[${status}] ${error.response.data}`
          : error.message;
      // No response at all (network failure) is also treated as transient/unavailable.
      notifyApproveError(user.email, !error.response || isServerErrorStatus(status), detail);
    } finally {
      setInProgress(false);
    }
  };

  const rejectUser = async (user_id: number | undefined) => {
    if (!user_id) {
      startNotification(t("platformAccess.userNotFound"), "error");
      return;
    }
    setInProgress(true);
    setRejectModalOptions({ id: undefined, isOpen: false });
    const user = records.find(user => user.id === user_id) || { email: "User not found" };
    try {
      const url = API_URL_USER_LIST + `${user_id}`;
      const response = await client.delete(url);
      if (response.status === 204) {
        listCIAMUsersPendingCreation();
        startNotification(t("platformAccess.rejectSuccess", { email: user.email }), "success");
      } else {
        notifyRejectError(user.email, response.data?.error_msg);
      }
    } catch (e: unknown) {
      const error = e as HttpError;
      // The reject endpoint returns proper HTTP statuses (204 ok, 4xx, 5xx), so the
      // status from the response object is meaningful here.
      const status = error.response?.status;
      const detail =
        status === 404
          ? "Error: Permission denied"
          : error.response && typeof error.response.data === "string"
            ? `[${status}] ${error.response.data}`
            : error.message;
      notifyRejectError(user.email, detail);
    } finally {
      setInProgress(false);
    }
  };

  const selectedRejectedRow = records.find(item => item.id === rejectModalOptions.id) || {
    first_name: "",
    last_name: "",
  };

  const selectedSort = sorting[0];
  const selectedSortColumn = selectedSort?.id ?? "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || PLATFORM_ACCESS_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  const visibleRecords = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();

    return query
      ? records.filter(record => (record.email ?? "").toLocaleLowerCase().includes(query))
      : records;
  }, [records, searchTerm]);

  return (
    <>
      <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 bg-blue-100 text-blue-600">
              <UserCheck />
            </span>
            <h1 className="font-semibold text-2xl">{t("platformAccess.pageTitle")}</h1>
          </div>
          <p className="text-muted-foreground">{t("platformAccess.description")}</p>
        </div>
        <div className="flex flex-col gap-3">
          {notificationState.state && (
            <Alert
              className="mb-8"
              variant={notificationState.type === "success" ? "success" : "destructive"}
            >
              {notificationState.type === "success" ? <CircleCheck /> : <TriangleAlert />}
              <AlertTitle>{t(`common.${notificationState.type}`)}</AlertTitle>
              <AlertDescription>
                <span>{notificationState.message}</span>
                {notificationState.detail && (
                  <span className="text-muted-foreground text-xs">{notificationState.detail}</span>
                )}
              </AlertDescription>
              <WFPButton
                variant="ghost"
                size="icon-xs"
                className="absolute top-2 right-2"
                aria-label={t("common.close") as string}
                onClick={closeNotification}
              >
                <X />
              </WFPButton>
            </Alert>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <InputGroup className="w-full max-w-xs sm:w-80">
            <InputGroupAddon>
              <Search className="size-4" aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              id="platform-access-search-input"
              type="search"
              value={searchTerm}
              className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              placeholder={t("platformAccess.searchPlaceholder")}
              aria-label={t("platformAccess.searchPlaceholder")}
              onChange={event => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={t("platformAccess.clearFilter")}
                  onClick={() => setSearchTerm("")}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
          <SortMenu
            options={PLATFORM_ACCESS_SORT_OPTIONS}
            sortColumn={selectedSortColumn}
            sortDirection={selectedSortDirection}
            onSortColumnChange={onSortColumnChange}
            onSortDirectionChange={onSortDirectionChange}
          />
        </div>
        <List
          isFetching={isFetching || inProgress}
          errorMessage={errorMessage}
          onSortingChange={onSortingChange}
          records={visibleRecords}
          sorting={sorting}
          submitUser={submitUser}
          setRejectModalOptions={setRejectModalOptions}
        />
      </main>
      <Dialog
        open={!!rejectModalOptions.isOpen && !!rejectModalOptions.id}
        onOpenChange={open => {
          if (!open) setRejectModalOptions({ isOpen: false, id: undefined });
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("common.reject") + " " + t("platformAccess.title")}</DialogTitle>
            <DialogDescription>
              {`${t("platformAccess.rejectText")}` +
                `${selectedRejectedRow.first_name} ${selectedRejectedRow.last_name}`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <WFPButton
              variant="outline"
              onClick={() => setRejectModalOptions({ isOpen: false, id: undefined })}
            >
              {t("common.cancel") as string}
            </WFPButton>
            <WFPButton variant="destructive" onClick={() => rejectUser(rejectModalOptions.id)}>
              {t("common.reject") as string}
            </WFPButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const mapStateToProps = (state: RootState) => {
  const pendingCreationState = state.listCIAMUsersPendingCreation;

  return {
    isFetching: pendingCreationState.isFetching,
    errorMessage: pendingCreationState.errorMessage,
    records: pendingCreationState.data
      ? (adaptCIAMUsers(pendingCreationState.data.users) as CIAMUser[])
      : [],
  };
};

// Maps functions to dispatch actions
export const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    listCIAMUsersPendingCreation: () => dispatchAction(dispatch, listCIAMUsersPendingCreation()),
  };
};

// The component uses the redux store
export const ConnectedPlatformAccess = connect(mapStateToProps, mapDispatchToProps)(PlatformAccess);
